/* CUSTOM SUPPLY-CHAIN-TIMELINE
Steuert die animierte Fortschrittslinie und Sichtbarkeit der Timeline-Items
innerhalb des Impact-Tabs auf der Produktdetailseite. */

(() => {
  /* CUSTOM TIMELINE-INITIALISIERUNG
  Sammelt alle relevanten DOM-Referenzen pro Timeline und beendet sich frueh,
  wenn auf der Seite kein passender Block vorhanden ist. */
  function initSupplyChainTimeline(root = document) {
    const wrappers = Array.from(root.querySelectorAll(".custom-supply-chain-wrapper"));

    if (!wrappers.length) {
      return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timelines = wrappers
      .map((wrapper) => {
        const timeline = wrapper.querySelector(".custom-supply-chain-timeline");
        const lineWrapper = wrapper.querySelector(".custom-supply-chain-timeline-line-block-wrapper");
        const lineBlock = wrapper.querySelector(".custom-supply-chain-timeline-line-block");
        const items = Array.from(wrapper.querySelectorAll(".custom-supply-chain-timeline-item"));

        if (!timeline || !lineWrapper || !lineBlock || !items.length) {
          return null;
        }

        return { wrapper, timeline, lineWrapper, lineBlock, items, lineMax: 0, startOffset: 0, endOffset: 0, panel: null };
      })
      .filter(Boolean);

    if (!timelines.length) {
      return;
    }

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    /* CUSTOM MARKER-OFFSETS
    Liest die CSS-Variablen der Timeline aus, damit JS und Styling synchron bleiben. */
    const markerCenterOffset = (entry) => {
      const styles = window.getComputedStyle(entry.wrapper);
      const dotSize = parseFloat(styles.getPropertyValue("--custom-supply-chain-dot-size")) || 12;
      const markerTop = parseFloat(styles.getPropertyValue("--custom-supply-chain-marker-top")) || 0;

      return markerTop + (dotSize / 2);
    };

    const markerBottomOffset = (entry) => {
      const styles = window.getComputedStyle(entry.wrapper);
      const dotSize = parseFloat(styles.getPropertyValue("--custom-supply-chain-dot-size")) || 12;
      const markerTop = parseFloat(styles.getPropertyValue("--custom-supply-chain-marker-top")) || 0;

      return markerTop + dotSize;
    };

    const isVisible = (element) => element.getClientRects().length > 0;

    /* CUSTOM MESSUNG
    Ermittelt Start, Ende und Maximalhoehe der vertikalen Fortschrittslinie. */
    const measureTimeline = (entry) => {
      if (!isVisible(entry.timeline)) {
        entry.lineMax = 0;
        entry.lineWrapper.style.height = "0px";
        entry.lineBlock.style.height = "0px";
        return;
      }

      const firstItem = entry.items[0];
      const lastItem = entry.items[entry.items.length - 1];
      const firstMarkerOffset = firstItem.offsetTop + markerBottomOffset(entry);
      const lastMarkerOffset = lastItem.offsetTop + markerCenterOffset(entry);
      const maxLine = Math.max(1, lastMarkerOffset - firstMarkerOffset);

      entry.lineMax = maxLine;
      entry.startOffset = firstMarkerOffset;
      entry.endOffset = lastMarkerOffset;
      entry.lineWrapper.style.top = `${firstMarkerOffset}px`;
      entry.lineWrapper.style.height = `${maxLine}px`;
      entry.lineBlock.style.height = "0px";
    };

    /* CUSTOM SCROLL-UPDATE
    Berechnet den sichtbaren Fortschritt relativ zum Viewport
    und markiert bereits erreichte Stationen. */
    const updateTimeline = (entry) => {
      if (!isVisible(entry.timeline)) {
        return;
      }

      if (entry.lineMax <= 0) {
        measureTimeline(entry);
      }

      const triggerY = window.innerHeight * 0.4;
      const timelineRect = entry.timeline.getBoundingClientRect();
      const start = timelineRect.top + entry.startOffset;
      const end = timelineRect.top + entry.endOffset;
      const progress = clamp((triggerY - start) / Math.max(1, end - start), 0, 1);
      const activeHeight = Math.min(entry.lineMax, Math.max(0, entry.lineMax * progress));

      entry.lineBlock.style.height = `${activeHeight}px`;

      entry.items.forEach((item) => {
        const itemMarkerY = timelineRect.top + item.offsetTop + markerCenterOffset(entry);
        const isItemVisible = triggerY >= itemMarkerY;
        item.classList.toggle("custom-is-visible", isItemVisible || prefersReducedMotion);
        item.classList.toggle("custom-is-reached", isItemVisible || prefersReducedMotion);
      });
    };

    const onFrame = () => {
      timelines.forEach(updateTimeline);
    };

    const remeasureAndUpdate = () => {
      timelines.forEach((entry) => measureTimeline(entry));
      requestFrameUpdate();
    };

    /* CUSTOM FRAME-SCHEDULING
    Fasst Scroll- und Resize-Updates in einem Animation-Frame zusammen. */
    let ticking = false;
    const requestFrameUpdate = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      requestAnimationFrame(() => {
        onFrame();
        ticking = false;
      });
    };

    remeasureAndUpdate();
    requestFrameUpdate();

    window.addEventListener("scroll", requestFrameUpdate, { passive: true });
    window.addEventListener("resize", () => {
      remeasureAndUpdate();
    });

    const resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(() => {
      remeasureAndUpdate();
    }) : null;

    timelines.forEach((entry) => {
      const images = entry.timeline.querySelectorAll("img");

      resizeObserver?.observe(entry.timeline);

      images.forEach((image) => {
        if (image.complete) {
          return;
        }

        image.addEventListener(
          "load",
          () => {
            remeasureAndUpdate();
          },
          { passive: true, once: true }
        );
      });
    });

    /* CUSTOM REAKTION AUF PANEL-WECHSEL
    Misst die Timeline neu, sobald der zugehoerige Tab sichtbar wird. */
    const panelObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          remeasureAndUpdate();
        });
      });
    });

    timelines.forEach((entry) => {
      entry.panel = entry.wrapper.closest('[data-tab-panel], [role="tabpanel"]');

      if (entry.panel) {
        panelObserver.observe(entry.panel, {
          attributes: true,
          attributeFilter: ["hidden", "class", "style", "aria-hidden"],
        });
      }
    });

    document.addEventListener("click", (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (!target.closest('[data-custom-tab-target="impact"], [data-tab-target="impact"]')) {
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          remeasureAndUpdate();
        });
      });
    });

    document.addEventListener("custom:product-tabs:panel-shown", () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          remeasureAndUpdate();
        });
      });
    });
  }

  /* CUSTOM BOOTSTRAP
  Initialisiert die Timeline einmalig nach dem Laden des Dokuments. */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initSupplyChainTimeline());
  } else {
    initSupplyChainTimeline();
  }
})();
